import {
  ShiftManagementTimeOffRowDocument,
  ShiftManagementTimeOffRowQuery,
  ShiftsLeaveFragment,
  ShiftsRestDayFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { MAX_ITEMS_LIMIT_NO_PAGINATION } from 'modules/shift_management/hooks/reducers/pagination'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Holiday from 'modules/shift_management/store/internal/models/Holiday'
import Leave from 'modules/shift_management/store/internal/models/Leave'

type TEmployeesPermissions =
  ShiftManagementTimeOffRowQuery['shiftManagement']['employeesPermissions'][number]

interface Result {
  leaves: Leaves
  restDays: RestDays
  holidays: Holidays
  loading: boolean
}

const useGetTimeOffRow = ({ skip }: { skip: boolean }): Result => {
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view

  const { loading, data } = useQuery(ShiftManagementTimeOffRowDocument, {
    variables: {
      page: 1,
      perPage: MAX_ITEMS_LIMIT_NO_PAGINATION,
      customFilters: filters.customFilters,
      employeeIds: filters.employeeIds?.map(Number) || null,
      endAt,
      endAtISO: endAt,
      logicOperator: filters.logicOperator,
      shiftIds: filters.shiftIds,
      startAt,
      startAtISO: startAt,
      terminated: filters.terminated,
    },
    skip,
  })

  const result: { leaves: Leaves; restDays: RestDays; holidays: Holidays } = useUnsafeMemo(() => {
    const employees = data?.shiftManagement?.employeesPermissions || []
    const leaves = new Leaves(
      employees.flatMap((employee: TEmployeesPermissions) =>
        employee.leaves.map((leave: ShiftsLeaveFragment) =>
          Leave.buildFromGraphQL(leave, employee.avatar?.url || '', employee.fullName)
        )
      )
    )

    const restDays = RestDays.build(
      employees.flatMap((employee: TEmployeesPermissions) =>
        employee.restDays.map((restDay: ShiftsRestDayFragment) => ({
          id: restDay.id,
          employee_id: restDay.employee?.id || 0,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        }))
      )
    )

    const holidays = new Holidays(
      compact(
        employees.flatMap((employee: TEmployeesPermissions) =>
          employee.location?.companyHolidays.map((holiday) =>
            Holiday.buildFromGraphQL(holiday, employee.location)
          )
        )
      )
    )

    return { leaves, restDays, holidays }
  }, [data?.shiftManagement?.employeesPermissions])

  return { leaves: result.leaves, restDays: result.restDays, holidays: result.holidays, loading }
}

export default useGetTimeOffRow
